import type { DataValue } from './dataValue'
import type { MetricLegacy } from './metric'
import type { Source } from './source'

// This should match "Errors" in types.gen.ts. We can't currently just use it
// directly as the types package can't import from api.
const factErrorTypes = [
  'Company always needs QA',
  'Flagged for review (not an analyst error)',
  'Great external comment',
  'Great mapping choice',
  'Great ticket resolution',
  'Inadequate ticket resolution',
  'Inappropriate external comment',
  'Industry always needs QA',
  'Low mapping confidence',
  'Missing or placeholder external comment',
  'Missing segments',
  'No independent anchor to verify value',
  'Premature update',
  'restatement', // Mildly annoying this isn't capitalised, but would require a BE data migration.
  'SDI revenue split drifted from ENTIS reference data',
  'Should be found',
  'Should not have been found',
  'Sustainability claim needs QA to check evidence',
  'Total does not match FactSet revenue',
  'Useful internal comment',
  'Wrong aggregation',
  'Wrong derivation status',
  'Wrong end time',
  'Wrong mapping confidence',
  'Wrong page selected',
  'Wrong qualifier used',
  'Wrong reason for large YoY change',
  'Wrong reporting period',
  'Wrong segments',
  'Wrong source reference for YoY change',
  'Wrong source used',
  'Wrong start time',
  'Wrong taxonomy - Major issue',
  'Wrong taxonomy - Minor issue',
  'Wrong units (for recorded value)',
  'Wrong value (for recorded value)',
] as const

export type FactErrorType = (typeof factErrorTypes)[number]

export type FactAnnotationType = 'High YoY variance'

export type Segment = {
  id: number
  name: string
  category?: string
  class?: string
  instance?: string
}

export type Aggregation = 'AVG' | 'DIFF' | 'NONE' | 'OTHER' | 'OR' | 'SUM'

type HowWasValueFoundAnswer = 'Metric page predictions' | 'Keyword search' | 'Other'
type YesNo = 'Yes' | 'No'

export type HowWasValueFound = {
  answer?: HowWasValueFoundAnswer | undefined
  keyword?: string | undefined
  other?: string | undefined
}

export type FactError = {
  id: number
  correct: boolean | null
  type: FactErrorType
  autoQAId: string | null
  cfUserId: number | null
  description: string | null
  extractionStepId: number | null
  npUserId: string | null
  severity: number | null
  selfAssessed: boolean
  jsonFeedback: {
    feedback?: string | undefined
    howWasValueFound?: HowWasValueFound | undefined
    wrongValueRepresents?: string | undefined
    wasSourceAlreadyUploaded?: YesNo | undefined
  } | null
}

export type SupportingFact = {
  id: number
  factId: Fact['id']
  recordedValue: DataValue
  standardizedValue: DataValue
  isTotal: boolean
  isDerived: boolean
  isLowConfidence: boolean
  externalComment: string | undefined
  internalComment: string | undefined
  qualifier: string
  segmentId: number | null
  segment?: Segment
  sourceId: number | undefined
  page: number | undefined
  taxonId: number | null
  mappingConfidence?: number | null
  isAiMapped: boolean
}

type BaseFact = {
  id: number
  value: DataValue
  start?: Date | undefined
  end: Date
  metricId: MetricLegacy['id']
  entityId: number
  year: number
  sourcesSearched?: number[]
  supportingFacts: SupportingFact[]
  aggregation: Aggregation
  status: FactState
}

export type Fact = BaseFact & {
  variant: 'Fact'
  lowConfidence?: boolean | undefined
  errors: FactError[]
  derived?: boolean | undefined
  // only true when a fact has been fetched from task endpoint
  taskId?: number
  metricName?: string
  feedback: string | null
  totalTimeSpent: string | null
  annotations: FactAnnotation[]
}

export type AcceptedFact = BaseFact & {
  variant: 'AcceptedFact'
  isHighYoY: boolean
  highYoYReasons: HighYoYReason[]
}

export const NO_REASON = 'No Apparent Reason'

type FactAnnotationBase = {
  id: number | undefined
  type: string | undefined
  detail: string
  explanation?: string | undefined
  factId?: Fact['id'] | undefined
}

export type FactAnnotation =
  | (FactAnnotationBase & {
      variant: 'base'
    })
  | (FactAnnotationBase & {
      variant: 'withReference'
      sourceId: number
      pageNum: number | undefined
      referenceId: number | undefined
      source?: Source | undefined
    })

export type HighYoYReason = FactAnnotation & { metricName: string | undefined }

//NOTE: any fields added here need to be in the reverseFieldMap
export type FactFilters = Fact & {
  // Alias for status due to Navigation table bug
  state: FactState
  stateNotEquals?: FactState
  entityName?: string
  entitySector?: string
  entityIndustry?: string
  metricName?: string
  metricActiveExtraction?: string
  withContextFacts?: boolean
  withErrors?: boolean
  valueUnit?: string
  valueUnitNotEquals?: string
  theme?: string
  startBefore?: string
  startAfter?: string
  endBefore?: string
  endAfter?: string
}

export enum FactState {
  Created = 'created',
  Extracted = 'extracted',
  Verified = 'verified',
  FlaggedForExpert = 'flagged for expert',
  Accepted = 'accepted',
  Deprecated = 'deprecated',
  Pending = 'pending',
  PrematureUpdate = 'premature update',
}

export type CreateFactsSetPayload = {
  entityId: number
  theme: string
  start: Date
  end: Date
}
